import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
//eslint-disable-next-line
import _ from 'lodash';
import ApplicationLayout from '../../components/layouts/ApplicationLayout/ApplicationLayout';
import { CommunityType } from '../../../types/community';
import { CreateBusinessForm, CreateCommunityForm } from '../../../types/forms';
import useAuth from '../../../hooks/account/useAuth';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import { wipeSelectedCommunity } from '../../../redux/slices/applicationSlice';
import { store } from '../../../redux/store';
import StepFlow from '../../components/misc/StepFlow';
import IntroductionView from './steps/IntroductionView';
import CommunitySetupView from './steps/CommunitySetupView';
import CommunityFeaturesView from './steps/CommunityFeaturesView';
import SubCommunitySetupView from './steps/SubCommunitySetupView';
import SubCommunitySubscriptionView from './steps/SubCommunitySubscriptionView';
import CommunitySubscriptionView from './steps/CommunitySubscriptionView';
import BusinessSubscriptionView from './steps/BusinessSubscriptionView';
import LoginRegisterView from './steps/LoginRegisterView';
import BusinessSetupView from './steps/BusinessSetupView';
import BusinessFeaturesView from './steps/BusinessFeaturesView';

export default function OnboardingPage(): JSX.Element {
  const auth = useAuth();
  const selectedBusiness = useSelectedBusiness();
  const location = useLocation();
  const [params] = useSearchParams();
  
  const communityMode = params.get('mode') === 'community';
  const isBusiness = process.env.REACT_APP_APPLICATION_VERSION === "BUSINESS";
  const type = params.get('type') as CommunityType;
  const [step, setStep] = useState<number>(0);
  const [community, setCommunity] = useState<CreateCommunityForm>({ name: '', description: '', enableDashboard: true });
  const [business, setBusiness] = useState<CreateBusinessForm>({ name: '', description: '' });

  const handleStep = (diff: 1 | -1) => setStep(step + diff);

  useLayoutEffect((): void => {
    if (location.state) {
      const data: CreateCommunityForm = location.state;

      setCommunity({ ...data, description: data.description || '' });
      setStep(3);
    } else if (type) {
      setCommunity((prevData: any) => ({ ...prevData, type }));
      setStep(2);
    }
  }, []);

  useEffect(() => {
    const code = params.get('code');
    if (code) {
      setCommunity((prevData: any) => ({ ...prevData, affiliateCode: code }));
    }
  }, [params]);

  useEffect(() => {
    if (!communityMode) {
      store.dispatch(wipeSelectedCommunity());
    }
  }, [communityMode]);

  let steps =[];
  if (!isBusiness) {
    steps = [
      !auth && <IntroductionView setStep={handleStep} dashboardType="community" />,
      <CommunitySetupView community={community} setCommunity={setCommunity} setStep={handleStep} />,
      <CommunityFeaturesView community={community} setCommunity={setCommunity} setStep={handleStep} />,
      <CommunitySubscriptionView community={community} setCommunity={setCommunity} setStep={handleStep} />,
      !auth && <LoginRegisterView community={community} setStep={handleStep} />,
    ].filter(Boolean) as JSX.Element[];
  } else if (communityMode) {
    steps = [
      <SubCommunitySetupView community={community} setCommunity={setCommunity} setStep={handleStep} businessType={selectedBusiness?.type} />,
      <SubCommunitySubscriptionView community={community} setCommunity={setCommunity} setStep={handleStep} />,
    ].filter(Boolean) as JSX.Element[];
  } else {
    steps = [
      !auth && <IntroductionView setStep={handleStep} dashboardType="business" />,
      <BusinessSetupView business={business} setBusiness={setBusiness} setStep={handleStep} />,
      <BusinessFeaturesView business={business} setBusiness={setBusiness} setStep={handleStep} />,
      <BusinessSubscriptionView business={business} setBusiness={setBusiness} setStep={handleStep} />,
      !auth && <LoginRegisterView business={business} setStep={handleStep} />,
    ].filter(Boolean) as JSX.Element[];
  }

  return (
    <ApplicationLayout collapsed={!communityMode}>
      <StepFlow steps={steps} indexState={[step, setStep]} />
    </ApplicationLayout>
  );
}